import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { gameAPI, questionsAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { 
    Clock, 
    Zap, 
    CheckCircle2, 
    XCircle, 
    ChevronRight,
    Trophy,
    Flame,
    ArrowLeft,
    Play,
    Target
} from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "../components/Brand";

const QUESTION_TIME = 30; // seconds per question

export default function GamePage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    
    // Game state
    const [gameState, setGameState] = useState("setup"); // setup, playing, result, summary
    const [mode, setMode] = useState(searchParams.get("mode") || "quick_play");
    const [subject, setSubject] = useState(searchParams.get("subject") || "all");
    const [subjects, setSubjects] = useState([]);
    
    // Session data
    const [sessionId, setSessionId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [answerResult, setAnswerResult] = useState(null);
    const [timeLeft, setTimeLeft] = useState(QUESTION_TIME);
    const [startTime, setStartTime] = useState(null);
    
    // Stats
    const [score, setScore] = useState(0);
    const [correctCount, setCorrectCount] = useState(0);
    const [streak, setStreak] = useState(0);
    const [loading, setLoading] = useState(false);
    
    // Load subjects
    useEffect(() => {
        const loadSubjects = async () => {
            try {
                const response = await questionsAPI.getSubjects();
                setSubjects(response.data.subjects);
            } catch (error) {
                console.error("Failed to load subjects:", error);
            }
        };
        loadSubjects();
    }, []);
    
    // Timer logic
    useEffect(() => {
        if (gameState !== "playing" || mode === "practice" || answerResult) return;
        
        if (timeLeft <= 0) {
            handleTimeUp();
            return;
        }
        
        const timer = setTimeout(() => {
            setTimeLeft(prev => prev - 1);
        }, 1000);
        
        return () => clearTimeout(timer);
    }, [timeLeft, gameState, mode, answerResult]);
    
    const handleTimeUp = async () => {
        if (!selectedOption && questions[currentIndex]) {
            // Auto-submit with no answer (wrong)
            await submitAnswer(null);
        }
    };
    
    const startGame = async () => {
        setLoading(true);
        try {
            const subjectParam = subject === "all" ? undefined : subject;
            const response = await gameAPI.startGame(mode, subjectParam);
            
            // Check if we got questions
            if (!response.data.questions || response.data.questions.length === 0) {
                toast.error("No questions available for this subject");
                toast.info("Try seeding questions from the dashboard first!");
                setLoading(false);
                return;
            }
            
            setSessionId(response.data.session_id);
            setQuestions(response.data.questions);
            setCurrentIndex(0);
            setScore(0);
            setCorrectCount(0);
            setStreak(0);
            setTimeLeft(QUESTION_TIME);
            setStartTime(Date.now());
            setGameState("playing");
        } catch (error) {
            const message = error.response?.data?.detail || "Failed to start game";
            toast.error(message);
            if (message.includes("No questions") || message.includes("not found")) {
                toast.info("Try seeding questions from the dashboard first!");
            }
        } finally {
            setLoading(false);
        }
    };
    
    const submitAnswer = async (optionId) => {
        if (!sessionId || !questions[currentIndex]) return;
        
        const timeTaken = (Date.now() - startTime) / 1000;
        setSelectedOption(optionId);
        
        try {
            const response = await gameAPI.submitAnswer(sessionId, {
                question_id: questions[currentIndex].id,
                selected_option_id: optionId || "none",
                time_taken: timeTaken
            });
            
            setAnswerResult(response.data);
            setScore(prev => prev + response.data.points_earned);
            
            if (response.data.is_correct) {
                setCorrectCount(prev => prev + 1);
                setStreak(prev => prev + 1);
            } else {
                setStreak(0);
            }
            
            setGameState("result");
        } catch (error) {
            toast.error("Failed to submit answer");
        }
    };
    
    const nextQuestion = () => {
        if (currentIndex + 1 >= questions.length) {
            endGame();
        } else {
            setCurrentIndex(prev => prev + 1);
            setSelectedOption(null);
            setAnswerResult(null);
            setTimeLeft(QUESTION_TIME);
            setStartTime(Date.now());
            setGameState("playing");
        }
    };
    
    const endGame = async () => {
        try {
            await gameAPI.endGame(sessionId);
            await refreshUser();
            setGameState("summary");
        } catch (error) {
            console.error("Failed to end game:", error);
            setGameState("summary");
        }
    };
    
    const currentQuestion = questions[currentIndex];
    const progressPercent = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
    
    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="sticky top-0 z-50 glass border-b border-slate-800 px-6 py-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                        <span className="hidden sm:inline">Back to Dashboard</span>
                    </Link>
                    
                    <BrandLogo />
                    
                    <div className="w-20" /> {/* Spacer */}
                </div>
            </nav>
            
            <main className="max-w-4xl mx-auto px-6 py-8">
                <AnimatePresence mode="wait">
                    {/* Setup Screen */}
                    {gameState === "setup" && (
                        <motion.div
                            key="setup"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="max-w-md mx-auto text-center"
                        >
                            <div className="mb-8">
                                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                                    {mode === "quick_play" ? (
                                        <Zap className="w-8 h-8 text-primary" />
                                    ) : (
                                        <Target className="w-8 h-8 text-secondary" />
                                    )}
                                </div>
                                <h1 className="font-heading text-3xl font-bold text-white mb-2">
                                    {mode === "quick_play" ? "Quick Play" : "Practice Mode"}
                                </h1>
                                <p className="text-slate-400">
                                    {mode === "quick_play" 
                                        ? "10 questions, 30 seconds each. Let's go!" 
                                        : "No time limit. Learn at your own pace."}
                                </p>
                            </div>
                            
                            <Card className="glass border-slate-800 rounded-2xl mb-6" data-testid="game-setup-card">
                                <CardContent className="p-6 space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-sm text-slate-400">Game Mode</label>
                                        <Select value={mode} onValueChange={setMode}>
                                            <SelectTrigger className="bg-slate-950/50 border-slate-800" data-testid="mode-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="quick_play">Quick Play (Timed)</SelectItem>
                                                <SelectItem value="practice">Practice (No Timer)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <label className="text-sm text-slate-400">Subject (Optional)</label>
                                        <Select value={subject} onValueChange={setSubject}>
                                            <SelectTrigger className="bg-slate-950/50 border-slate-800" data-testid="subject-select">
                                                <SelectValue placeholder="All Subjects" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Subjects</SelectItem>
                                                {subjects.map((s) => (
                                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Button
                                onClick={startGame}
                                disabled={loading}
                                size="lg"
                                className="rounded-full bg-primary hover:bg-primary/90 glow-primary w-full sm:w-auto px-8"
                                data-testid="start-game-btn"
                            >
                                {loading ? (
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <>
                                        <Play className="w-5 h-5 mr-2" />
                                        Start Game
                                    </>
                                )}
                            </Button>
                        </motion.div>
                    )}
                    
                    {/* Playing / Result Screen */}
                    {(gameState === "playing" || gameState === "result") && currentQuestion && (
                        <motion.div
                            key="playing"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            {/* Progress Bar */}
                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-slate-400 text-sm">
                                        Question {currentIndex + 1} of {questions.length}
                                    </span>
                                    <div className="flex items-center gap-4">
                                        {streak > 0 && (
                                            <Badge className="bg-orange-500/20 text-orange-400 border-0">
                                                <Flame className="w-3 h-3 mr-1" />
                                                {streak} streak
                                            </Badge>
                                        )}
                                        <span className="font-mono text-primary font-bold" data-testid="score-display">
                                            {score.toLocaleString()} pts
                                        </span>
                                    </div>
                                </div>
                                <Progress value={progressPercent} className="h-2 bg-slate-800" />
                            </div>
                            
                            {/* Timer */}
                            {mode === "quick_play" && gameState === "playing" && (
                                <div className="flex justify-center mb-6">
                                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                                        timeLeft <= 10 ? "bg-red-500/20 text-red-400" : "bg-slate-800 text-slate-300"
                                    }`}>
                                        <Clock className="w-4 h-4" />
                                        <span className="font-mono font-bold" data-testid="timer-display">{timeLeft}s</span>
                                    </div>
                                </div>
                            )}
                            
                            {/* Question Card */}
                            <Card className="glass border-slate-800 rounded-2xl mb-6" data-testid="question-card">
                                <CardContent className="p-6">
                                    <Badge className="mb-4 bg-slate-800 text-slate-400 border-0">
                                        {currentQuestion.subject}
                                    </Badge>
                                    <p className="text-white text-lg leading-relaxed" data-testid="question-text">
                                        {currentQuestion.text}
                                    </p>
                                </CardContent>
                            </Card>
                            
                            {/* Options */}
                            <div className="space-y-3 mb-6">
                                {currentQuestion.options.map((option, index) => {
                                    const isSelected = selectedOption === option.id;
                                    const isCorrect = answerResult?.correct_option_id === option.id;
                                    const isWrong = isSelected && !answerResult?.is_correct;
                                    
                                    let optionClass = "glass border-slate-800 rounded-xl p-4 cursor-pointer transition-all";
                                    if (gameState === "result") {
                                        if (isCorrect) {
                                            optionClass += " border-emerald-500 bg-emerald-500/10";
                                        } else if (isWrong) {
                                            optionClass += " border-red-500 bg-red-500/10";
                                        }
                                    } else if (isSelected) {
                                        optionClass += " border-primary ring-2 ring-primary/50";
                                    } else {
                                        optionClass += " hover:border-primary/50";
                                    }
                                    
                                    return (
                                        <motion.div
                                            key={option.id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                            className={optionClass}
                                            onClick={() => gameState === "playing" && submitAnswer(option.id)}
                                            data-testid={`option-${option.id}`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-mono font-bold ${
                                                    gameState === "result" && isCorrect
                                                        ? "bg-emerald-500 text-white"
                                                        : gameState === "result" && isWrong
                                                        ? "bg-red-500 text-white"
                                                        : isSelected
                                                        ? "bg-primary text-white"
                                                        : "bg-slate-800 text-slate-400"
                                                }`}>
                                                    {option.id}
                                                </span>
                                                <span className="text-white pt-1">{option.text}</span>
                                                {gameState === "result" && isCorrect && (
                                                    <CheckCircle2 className="w-5 h-5 text-emerald-400 ml-auto flex-shrink-0" />
                                                )}
                                                {gameState === "result" && isWrong && (
                                                    <XCircle className="w-5 h-5 text-red-400 ml-auto flex-shrink-0" />
                                                )}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                            
                            {/* Result Explanation */}
                            {gameState === "result" && answerResult && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    <Card className={`rounded-2xl mb-6 ${
                                        answerResult.is_correct 
                                            ? "bg-emerald-500/10 border-emerald-500/30" 
                                            : "bg-red-500/10 border-red-500/30"
                                    }`} data-testid="result-card">
                                        <CardContent className="p-6">
                                            <div className="flex items-center gap-3 mb-3">
                                                {answerResult.is_correct ? (
                                                    <>
                                                        <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                                                        <span className="font-heading font-bold text-emerald-400">Correct!</span>
                                                        <span className="ml-auto font-mono text-emerald-400">
                                                            +{answerResult.points_earned} pts
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <XCircle className="w-6 h-6 text-red-400" />
                                                        <span className="font-heading font-bold text-red-400">Incorrect</span>
                                                    </>
                                                )}
                                            </div>
                                            <p className="text-slate-300 text-sm leading-relaxed">
                                                {answerResult.explanation}
                                            </p>
                                        </CardContent>
                                    </Card>
                                    
                                    <Button
                                        onClick={nextQuestion}
                                        size="lg"
                                        className="w-full rounded-full bg-primary hover:bg-primary/90"
                                        data-testid="next-question-btn"
                                    >
                                        {currentIndex + 1 >= questions.length ? "See Results" : "Next Question"}
                                        <ChevronRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </motion.div>
                            )}
                        </motion.div>
                    )}
                    
                    {/* Summary Screen */}
                    {gameState === "summary" && (
                        <motion.div
                            key="summary"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="max-w-md mx-auto text-center"
                        >
                            <div className="mb-8">
                                <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4 animate-pulse-glow">
                                    <Trophy className="w-10 h-10 text-primary" />
                                </div>
                                <h1 className="font-heading text-3xl font-bold text-white mb-2">
                                    Game Complete!
                                </h1>
                            </div>
                            
                            <Card className="glass border-slate-800 rounded-2xl mb-6" data-testid="summary-card">
                                <CardContent className="p-6 space-y-4">
                                    <div className="text-center pb-4 border-b border-slate-800">
                                        <p className="font-heading text-5xl font-black gradient-text mb-1">
                                            {score.toLocaleString()}
                                        </p>
                                        <p className="text-slate-400">Total Points</p>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="text-center p-3 rounded-xl bg-slate-800/40">
                                            <p className="font-heading text-2xl font-bold text-emerald-400">
                                                {correctCount}/{questions.length}
                                            </p>
                                            <p className="text-slate-400 text-sm">Correct</p>
                                        </div>
                                        <div className="text-center p-3 rounded-xl bg-slate-800/40">
                                            <p className="font-heading text-2xl font-bold text-white">
                                                {Math.round((correctCount / questions.length) * 100)}%
                                            </p>
                                            <p className="text-slate-400 text-sm">Accuracy</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Button
                                    onClick={() => {
                                        setGameState("setup");
                                        setSessionId(null);
                                        setQuestions([]);
                                    }}
                                    size="lg"
                                    className="flex-1 rounded-full bg-primary hover:bg-primary/90"
                                    data-testid="play-again-btn"
                                >
                                    <Play className="w-5 h-5 mr-2" />
                                    Play Again
                                </Button>
                                <Link to="/dashboard" className="flex-1">
                                    <Button
                                        variant="outline"
                                        size="lg"
                                        className="w-full rounded-full border-slate-700 hover:border-primary"
                                        data-testid="back-to-dashboard-btn"
                                    >
                                        Dashboard
                                    </Button>
                                </Link>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
}
